/**
 * What a ticket's response-time promise looks like on screen.
 *
 * Two shapes, because a queue and a detail page want opposite things from the
 * same data:
 *
 *  - `SlaFlag` is for a LIST ROW and draws nothing unless the promise is nearly
 *    out or already broken. A queue where every row wears a badge is a queue
 *    with no signal in it, so a promise being kept is silent - the row's job is
 *    to make the two tickets that need somebody stand out from the forty that
 *    do not.
 *  - `SlaLines` is for the DETAIL, where the question is not "does this need me"
 *    but "how are we doing on this one", and both clocks are worth stating even
 *    when the answer is that everything is fine.
 *
 * Neither invents a verdict. The states arrive already decided by the server
 * (`crm-support/lib/sla.ts`), so the badge and the `?sla=breached` filter cannot
 * drift into disagreeing about what "late" means.
 */
import { AlarmClock, CheckCircle2, Clock } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { formatSlaGap, slaTone, worstVerdict, type SlaVerdict, type TicketSla } from "../../../lib/sla";
import { StatusPill, type StatusTone } from "../../ui";

/**
 * The wording for one verdict, which depends on which side of zero it is.
 *
 * `minutesLeft` is signed and `formatSlaGap` drops the sign, so the sentence
 * around it is what says whether this is time remaining or time already lost.
 * Splitting it this way means the number never has to carry a minus into a badge
 * that already reads "late".
 */
function verdictText(t: ReturnType<typeof useT>, clock: "firstResponse" | "resolve", v: SlaVerdict): string {
  const what = t(`support.sla.clock.${clock}`);
  if (v.minutesLeft === null) return what;
  const gap = formatSlaGap(v.minutesLeft);
  if (v.state === "breached") return t("support.sla.lateBy", { what, gap });
  if (v.state === "met") return t("support.sla.metWith", { what, gap });
  return t("support.sla.leftOf", { what, gap });
}

/**
 * The one badge a queue row gets, or nothing.
 *
 * `due` and `breached` only. `ok` is the normal case and `met` is a finished
 * one; drawing either would put a pill on almost every row, and the two that
 * matter would stop being visible at a glance - which is the entire reason the
 * column exists.
 */
export function SlaFlag({ sla }: { sla: TicketSla | undefined }) {
  const t = useT();
  const worst = worstVerdict(sla);
  if (!worst) return null;
  if (worst.verdict.state !== "due" && worst.verdict.state !== "breached") return null;

  const late = worst.verdict.state === "breached";
  return (
    <span
      className="inline-flex items-center gap-1"
      title={verdictText(t, worst.clock, worst.verdict)}
    >
      {late ? (
        <AlarmClock className="size-3 text-destructive" />
      ) : (
        <Clock className="size-3 text-amber-600 dark:text-amber-400" />
      )}
      <span className={late ? "crm-tnum font-semibold text-destructive" : "crm-tnum text-amber-600 dark:text-amber-400"}>
        {formatSlaGap(worst.verdict.minutesLeft ?? 0)}
      </span>
    </span>
  );
}

/** One clock as a pill plus its sentence. `na` draws nothing - see `slaTone`. */
function SlaLine({ clock, verdict }: { clock: "firstResponse" | "resolve"; verdict: SlaVerdict }) {
  const t = useT();
  if (verdict.state === "na") return null;
  return (
    <div className="flex items-center gap-2 text-[12.5px]">
      <StatusPill tone={slaTone(verdict.state) as StatusTone}>
        {t(`support.sla.state.${verdict.state}`)}
      </StatusPill>
      <span className="crm-mut">{verdictText(t, clock, verdict)}</span>
      {verdict.state === "met" ? <CheckCircle2 className="size-3.5 text-emerald-600 dark:text-emerald-400" /> : null}
    </div>
  );
}

/**
 * Both clocks on the detail page, or nothing at all.
 *
 * "Nothing at all" is the honest answer for a priority the desk made no promise
 * about. A row saying "no target" would be a label explaining an absence, which
 * takes up space on every ticket of that priority forever to tell the reader
 * something the settings panel already knows.
 */
export function SlaLines({ sla }: { sla: TicketSla | undefined }) {
  if (!sla) return null;
  if (sla.firstResponse.state === "na" && sla.resolve.state === "na") return null;
  return (
    <div className="flex flex-col gap-1.5">
      <SlaLine clock="firstResponse" verdict={sla.firstResponse} />
      <SlaLine clock="resolve" verdict={sla.resolve} />
    </div>
  );
}
